import React, { useContext, useEffect, useRef, useState } from 'react';
import "../../../styles/admin/Transaction.css";
import { searchTransactionsByTicket, searchTransactionsUsers, isFreePlayTransaction } from '../../../services/admin/Transaction.tsx';
import { Users, Transaction } from '../../../models/User.tsx';
import * as XLSX from 'xlsx';
import { RiFileExcel2Line } from "react-icons/ri";
import { FiAlertCircle } from "react-icons/fi";
import { LuCalendarSearch } from "react-icons/lu";
import { formatNumbers } from '../../../helpers/functions.tsx';
import {
    adminDateInputToUtc,
    formatAdminFiguresDateTime,
    formatAdminFiguresDateLabel,
    formatAdminFiguresTime,
    getAdminFiguresDateKey,
} from '../../../helpers/adminFiguresTime.tsx';
import { useUser } from '../../../context/UserContext.tsx';
import AgentContext from '../../../context/admin/Agent.tsx';
import { filterMessagingUsers } from '../../../helpers/messagingRecipients.ts';
import { FaSyncAlt } from 'react-icons/fa';

const TRANSACTION_KIND_OPTIONS = [
    { value: 'deposit', label: 'Deposits' },
    { value: 'withdrawal', label: 'Withdrawals' },
    { value: 'DebitAdjustment', label: 'Debit Adjustment' },
    { value: 'CreditAdjustment', label: 'Credit Adjustment' },
    { value: 'zeroOut', label: 'Zero Out' },
    { value: 'Zero Balance', label: 'Bets' },
] as const;

type TransactionKindValue = typeof TRANSACTION_KIND_OPTIONS[number]['value'];

const createDefaultKindToggles = (): Record<TransactionKindValue, boolean> =>
    Object.fromEntries(TRANSACTION_KIND_OPTIONS.map(({ value }) => [value, true])) as Record<TransactionKindValue, boolean>;

const filterByTransactionKinds = (transactions: Transaction[], selectedKinds: TransactionKindValue[]): Transaction[] => {
    if (selectedKinds.length === 0 || selectedKinds.length === TRANSACTION_KIND_OPTIONS.length) {
        return transactions;
    }

    const selected = new Set<string>(selectedKinds);
    return transactions.filter(transaction => selected.has(transaction.transactionType));
};

const formatTransactionTypeLabel = (transaction: Transaction): string => {
    if (isFreePlayTransaction(transaction)) {
        return transaction.transactionType === 'withdrawal' ? 'Free Play Withdrawal' : 'Free Play';
    }
    if (transaction.transactionType === 'zeroOut') return 'Zero Out';
    return transaction.transactionType;
};

const formatTransactionDescription = (transaction: Transaction): string => {
    if (isFreePlayTransaction(transaction)) {
        return transaction.transactionType === 'withdrawal' ? 'Free Play Withdrawal' : 'Free Play Deposit';
    }
    return transaction.description;
};

const TransactionBody = () => {

    const { user: currentUser } = useUser();
    const todayKey = getAdminFiguresDateKey(new Date());
    const { allAgents, allPlayers } = useContext(AgentContext)
    const [startDate, setStartDate] = useState<string>(todayKey);
    const [endDate, setEndDate] = useState<string>(todayKey);
    const [transactionType, setTransactionType] = useState('playersTransaction');
    const [kindToggles, setKindToggles] = useState<Record<TransactionKindValue, boolean>>(createDefaultKindToggles);
    const [kindDropdownOpen, setKindDropdownOpen] = useState(false);
    const kindDropdownRef = useRef<HTMLDivElement>(null);
    const [agentName, setAgentName] = useState('');
    const [playerName, setPlayerName] = useState('');
    const [ticketQuery, setTicketQuery] = useState('');
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [agentResults, setAgentResults] = useState<Users[]>([]);
    const [playerResults, setPlayerResults] = useState<Users[]>([]);
    const [activeInput, setActiveInput] = useState<'agent' | 'player' | null>(null);
    const [inputsEnabled, setInputsEnabled] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [noDataMessage, setNoDataMessage] = useState('');

    useEffect(() => {
        if (!kindDropdownOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (kindDropdownRef.current && !kindDropdownRef.current.contains(event.target as Node)) {
                setKindDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [kindDropdownOpen]);

    const getActiveTransactionKinds = (): TransactionKindValue[] =>
        TRANSACTION_KIND_OPTIONS
            .filter(option => kindToggles[option.value])
            .map(option => option.value);

    const handleKindToggle = (kind: TransactionKindValue) => {
        setKindToggles(prev => ({
            ...prev,
            [kind]: !prev[kind],
        }));
    };


    const buildAdminDate = (dateString: string, isEnd = false) =>
        adminDateInputToUtc(dateString, isEnd);

    const buildCustomerUserNames = (): string[] => {
        if (currentUser?.userType === 'masterAgent') {
            return [
                ...allAgents.map((user: Users) => user.userName),
                ...allPlayers.map((user: Users) => user.userName),
            ];
        }
        if (currentUser?.userType === 'subAgent') {
            return allPlayers.map((user: Users) => user.userName);
        }
        return [];
    };

    const handleSearch = async () => {
        if (!currentUser || !currentUser.userType || !currentUser.userName) {
            console.error('User type or name is undefined');
            return;
        }
        const { userType, userName } = currentUser;
        setIsLoading(true);
        setNoDataMessage('');
        try {
            const trimmedTicket = ticketQuery.trim();
            let result: Transaction[];

            if (trimmedTicket) {
                result = await searchTransactionsByTicket(
                    trimmedTicket,
                    userName,
                    userType,
                    transactionType,
                    buildCustomerUserNames()
                );
            } else {
                const specificUser = activeInput === 'agent' ? agentName : playerName;
                const start = buildAdminDate(startDate);
                const end = buildAdminDate(endDate, true);

                result = await searchTransactionsUsers(
                    userName,
                    start.toISOString(),
                    end.toISOString(),
                    userType,
                    transactionType,
                    specificUser || undefined,
                    specificUser ? undefined : buildCustomerUserNames()
                );
            }

            const filteredResult = filterByTransactionKinds(result, getActiveTransactionKinds());
            setTransactions(filteredResult);
            if (filteredResult.length === 0) {
                setNoDataMessage('No transactions found.');
                setTimeout(() => {
                    setNoDataMessage('');
                }, 5000);
            } else {
                setNoDataMessage('');
            }
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const searchUsers = (queryText: string, inputType: 'agent' | 'player') => {
        if (!queryText) {
            if (inputType === 'agent') setAgentResults([]);
            else setPlayerResults([]);
            return;
        }

        if (inputType === 'agent') {
            setAgentResults(filterMessagingUsers(allAgents, queryText));
        } else {
            setPlayerResults(filterMessagingUsers(allPlayers, queryText));
        }
    };
    const handleSelectUser = (user: any, inputType: any) => {
        if (inputType === 'agent') {
            setAgentName(user.userName);
            setAgentResults([]);
        } else if (inputType === 'player') {
            setPlayerName(user.userName);
            setPlayerResults([]);
        }
    };

    const handleInputChange = (e: any) => {
        const { name, value } = e.target;

        if (name === 'agent') {
            setAgentName(value);
            setPlayerName('');
            searchUsers(value, 'agent');
        } else if (name === 'player') {
            setPlayerName(value);
            setAgentName('');
            searchUsers(value, 'player');
        }
    };

    const handleTransactionTypeChange = (e: any) => {
        const selectedType = e.target.value;
        setTransactionType(selectedType);
        setInputsEnabled(selectedType !== 'deletedTransactions');

        if (selectedType === 'deletedTransactions') {
            setAgentName('');
            setPlayerName('');
            setAgentResults([]);
            setPlayerResults([]);
        }
    };

    const exportToExcel = () => {
        const input = document.querySelector('.table');
        if (input) {

            const tableClone = input.cloneNode(true) as HTMLElement;
            const ws = XLSX.utils.table_to_sheet(tableClone);

            ws['!cols'] = [
                { wpx: 125 }, // Date
                { wpx: 125 }, // Agent
                { wpx: 125 }, // Customer
                { wpx: 100 }, // Ticket
                { wpx: 125 },  // Transaction
                { wpx: 125 },  // Description
                { wpx: 125 },  // Amount
                { wpx: 125 },  // Entered By
            ];

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, currentUser?.userName);

            const today = new Date();
            const formattedDate = formatAdminFiguresDateLabel(today, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            });
            const formattedTime = formatAdminFiguresTime(today, {
                hour: 'numeric',
                minute: '2-digit',
            });

            const cleanTime = formattedTime.replace(':', '_').replace(' ', '');
            const fileName = `TransactionHistory_ ${formattedDate.replace(',', '')} ${cleanTime}.xlsx`;

            XLSX.writeFile(wb, fileName);
        } else {
            console.error("Item not found");
        }
    };

    return (
        <>
            {isLoading ? (
                <div className="loader-container">
                    <FaSyncAlt className="loader-icon-proyect" />
                </div>
            ) : (
                <div className='container-fluid'>
                    <div className='mt-2 container-body'>
                        <div className="userPendig-container mt-1">
                            <div className="user-row">
                                <div className="input-group input-group-sm flex-nowrap">
                                    <span className="input-group-text body-customer">Agents</span>
                                    <input
                                        className="inputTransaction body-customer"
                                        placeholder="Search accounts"
                                        name="agent"
                                        value={agentName}
                                        onFocus={() => setActiveInput('agent')}
                                        onChange={handleInputChange}
                                        disabled={!inputsEnabled}
                                        autoComplete="off" />
                                    {activeInput === 'agent' && agentResults.length > 0 && (
                                        <ul className="list-group overflow-auto" style={{ maxHeight: "300px" }}>
                                            {agentResults.map((user) => (
                                                <li key={user.id ?? user.userName} className="list-group-item" onClick={() => handleSelectUser(user, 'agent')}>                                                    <div className='d-flex justify-content-between'>
                                                        <div>
                                                            <span className='search-username-navbar'>{user.userName}</span>
                                                            <span className='search-pin-name-navbar'>({user.pin})</span>
                                                        </div>

                                                        <div>
                                                            <span className='search-pin-name-navbar'>{user.name}</span>
                                                        </div>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                <div className="input-group input-group-sm flex-nowrap">
                                    <span className="body-customer input-group-text">Players</span>
                                    <input
                                        className="inputTransaction body-customer"
                                        placeholder="Search accounts"
                                        name="player"
                                        value={playerName}
                                        onFocus={() => setActiveInput('player')}
                                        onChange={handleInputChange}
                                        disabled={!inputsEnabled}
                                        autoComplete="off" />
                                    {activeInput === 'player' && playerResults.length > 0 && (
                                        <ul className="list-group overflow-auto" style={{ maxHeight: "300px" }}>
                                            {playerResults.map((user) => (
                                                <li key={user.id ?? user.userName} className="list-group-item" onClick={() => handleSelectUser(user, 'player')}>                                                    <div className='d-flex justify-content-between'>
                                                        <div>
                                                            <span>{user.userName}</span>
                                                            <span className='search-pin-name'>({user.pin})</span>
                                                        </div>

                                                        <div>
                                                            <span className='search-pin-name'>{user.name}</span>
                                                        </div>
                                                    </div>

                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                <div className="input-group input-group-sm flex-nowrap">
                                    <span className="body-customer input-group-text">Ticket</span>
                                    <input
                                        className="inputTransaction body-customer"
                                        placeholder="Search by ticket"
                                        name="ticket"
                                        value={ticketQuery}
                                        onChange={(e) => setTicketQuery(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleSearch();
                                            }
                                        }}
                                        autoComplete="off"
                                    />
                                </div>
                            </div>

                            <div className="user-row transaction-filters-row">
                                <div className="transaction-filters-group">
                                    <div className="colum-pending transaction-kind-dropdown" ref={kindDropdownRef}>
                                        <button
                                            type="button"
                                            className="selectTransaction body-customer transaction-kind-trigger"
                                            onClick={() => setKindDropdownOpen(prev => !prev)}
                                        >
                                            Transaction Type
                                        </button>
                                        {kindDropdownOpen && (
                                            <div className="transaction-kind-panel">
                                                {TRANSACTION_KIND_OPTIONS.map((option) => (
                                                    <div key={option.value} className="transaction-kind-option">
                                                        <span className="transaction-kind-label">{option.label}</span>
                                                        <div className="form-switch">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id={`transaction-kind-${option.value}`}
                                                                checked={kindToggles[option.value]}
                                                                onChange={() => handleKindToggle(option.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="transaction-scope-group">
                                        <div className="colum-pending transaction-scope-select">
                                            <select id="transaction" name="transaction" className="selectTransaction body-customer" value={transactionType} onChange={handleTransactionTypeChange}>
                                                <option value="playersTransaction">Player Transactions</option>
                                                <option value="agentsTransaction">Agent Transactions</option>
                                                <option value="freePlayTransactions">Free Play</option>
                                                <option value="deletedTransactions">Transactions Deleted</option>
                                            </select>
                                        </div>
                                        <div className="excel-icon d-none d-md-flex justify-content-center align-items-center" onClick={exportToExcel}>
                                            <RiFileExcel2Line className='excel-transaction' />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="user-row mt-1 d-flex align-items-center" >
                                <div className="colum-pending">
                                    <input type="date" id="startDate" name="startDate" className="calendar-control"
                                        value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                                </div>
                                <div className="colum-pending">
                                    <span className='fontSize'>To</span>
                                </div>
                                <div className="colum-pending">
                                    <input type="date" id="endDate" name="endDate" className="calendar-control"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                    />
                                </div>
                                <div className="colum-pending">
                                    <button className="btn-transaction" onClick={handleSearch}><LuCalendarSearch /></button>
                                </div>
                            </div>
                        </div>

                        <div className="table-responsive">
                            <table className="mt-4 table table-user-activity">
                                <thead className="header-customer">
                                    <tr>
                                        <th className="date-column-transaction">Date (PST)</th>
                                        <th className="agent-column-transaction">Agent</th>
                                        <th className="customer-column-transaction">Customer</th>
                                        <th className="ticket-column-transaction">Ticket</th>
                                        <th className="transaction-column-transaction">Transaction</th>
                                        <th className="description-column-transaction">Description</th>
                                        <th className="amount-column-transaction">Amount</th>
                                        <th className="by-column-transaction">Entered By</th>
                                    </tr>
                                </thead>
                                <tbody className="body-customer">
                                    {transactions.length > 0 ? (
                                        transactions.map((transaction) => (
                                            <tr key={transaction.id}>
                                                <td className="date-column-transaction">
                                                    {formatAdminFiguresDateTime(transaction.createDate.toDate(), {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric',
                                                        hour: 'numeric',
                                                        minute: 'numeric',
                                                        hour12: true,
                                                    })}
                                                </td>
                                                <td className="agent-column-transaction">{transaction.agent}</td>
                                                <td className="customer-column-transaction">{transaction.customer}</td>
                                                <td className="ticket-column-transaction">{transaction.ticket || '-'}</td>
                                                <td className="transaction-column-transaction">{formatTransactionTypeLabel(transaction)}</td>
                                                <td className="description-column-transaction">{formatTransactionDescription(transaction)}</td>
                                                <td className="amount-column-transaction">{formatNumbers(transaction.amount)}</td>
                                                <td className="by-column-transaction">{transaction.createBy}</td>
                                            </tr>
                                        ))
                                    ) : null}
                                </tbody>
                            </table>
                        </div>
                        {noDataMessage && (
                            <div className="no-data-message">
                                <div className="no-data-message-content">
                                    <FiAlertCircle className="no-data-icon" />
                                    <span>{noDataMessage}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    )
}

export default TransactionBody;